// src/AuthContext.js
/**
 * Auth context — provides {user, token, tier, credits, login, logout} to the app.
 *
 * Google Sign-In flow:
 * 1. User clicks "Sign in with Google" → Google Identity Services popup
 * 2. Google returns ID token → POST /auth/google
 * 3. Backend verifies, returns app JWT + user info
 * 4. JWT stored in localStorage, user/tier in state
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from './api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'phutball_auth_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tier, setTier] = useState('free');
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));
  const gsiInitialized = useRef(false);
  const loginRef = useRef(null);

  // On mount: if we have a stored token, fetch user info
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetch('/auth/me', { token })
      .then(data => {
        setUser(data.user); // includes elo_rating, elo_games_played, country_code, region_code
        setTier(data.tier || 'free');
        setCredits(data.credits || []);
      })
      .catch(() => {
        // Token expired or invalid
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (googleIdToken) => {
    const data = await apiFetch('/auth/google', {
      method: 'POST',
      body: { id_token: googleIdToken },
    });
    const { token: appToken, user: userInfo } = data;
    localStorage.setItem(TOKEN_KEY, appToken);
    setToken(appToken);
    setUser(userInfo);

    // Fetch tier/credits + elo/geo
    const me = await apiFetch('/auth/me', { token: appToken });
    setTier(me.tier || 'free');
    setCredits(me.credits || []);
    // Merge elo/geo fields into user state
    setUser(prev => ({ ...prev, ...me.user }));

    return me.user;
  }, []);

  // Keep loginRef current so the GSI callback always uses the latest login
  loginRef.current = login;

  const [gsiReady, setGsiReady] = useState(!!window.google?.accounts?.id);

  // Wait for Google Identity Services script to load, then initialize
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const init = () => {
      if (gsiInitialized.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            await loginRef.current(response.credential);
          } catch (e) {
            console.error('Google sign-in failed:', e);
          }
        },
      });
      gsiInitialized.current = true;
      setGsiReady(true);
    };

    if (window.google?.accounts?.id) {
      init();
    } else {
      // Script not loaded yet — poll until ready
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          init();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  // Render a Google Sign-In button into a container element
  const renderGoogleButton = useCallback((element, options = {}) => {
    if (!element || !window.google?.accounts?.id) return;
    window.google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: 'filled_black',
      shape: 'pill',
      text: 'signin',
      size: 'medium',
      ...options,
    });
  }, [gsiReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger Google's One Tap / account chooser from a custom button.
  // Used by custom-styled sign-in buttons that match the site theme.
  const promptGoogleSignIn = useCallback(() => {
    if (!window.google?.accounts?.id) return false;
    try {
      window.google.accounts.id.prompt();
      return true;
    } catch (e) {
      console.warn('Google One Tap prompt failed:', e);
      return false;
    }
  }, [gsiReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setTier('free');
    setCredits([]);
  }, []);

  const refreshCredits = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch('/credits', { token });
      setCredits(data.credits || []);
    } catch {
      // ignore
    }
  }, [token]);

  const updateSettings = useCallback(async (settings) => {
    if (!token) return;
    const data = await apiFetch('/settings', { method: 'POST', token, body: settings });
    if (data.user) {
      setUser(prev => ({ ...prev, ...data.user, leaderboard_visible: !!data.user.leaderboard_visible }));
    }
  }, [token]);

  const value = { user, token, tier, credits, loading, login, logout, refreshCredits, updateSettings, renderGoogleButton, promptGoogleSignIn, gsiReady };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
